import React, { useState, useRef, useEffect, useMemo } from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Dashboard, ListAlt, Image as ImageIcon, InsertDriveFile, Label, AccountCircle, Settings, ExitToApp, Person, Assignment, Description, ViewQuilt, FileCopy, Slideshow, SmartDisplay, Folder, Close as CloseIcon, ArrowForward, Celebration, RestartAlt, MenuBook, GpsFixed, Palette, Article, Collections, LocalOffer } from '@mui/icons-material';
import { Avatar, Menu, MenuItem, Stack, Tooltip, Button, Popover, Paper, Fade, Zoom } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { logout } from '../../services/auth';
import { useAppContext } from '../../contexts/AppContext';
import { useLang } from '../../contexts/langContext';
import { useBranding } from '../../contexts/BrandingContext';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { lang } = useLang();
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { appTitle } = useAppContext();
  const branding = useBranding();
  const showOnboardingTips = branding.defaultPreferences?.showOnboardingTips;
  const enableImageGeneration = branding.features?.enableImageGeneration;
  const displayProductConcept = branding.features?.displayProductConcept;

  // Refs for onboarding tour
  const trainingBtnRef = useRef<HTMLAnchorElement | null>(null);
  const navItemRefs = useRef<(HTMLElement | null)[]>([]);

  // Onboarding tour state
  const [tourOpen, setTourOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourSteps, setTourSteps] = useState<Array<{
    ref: React.RefObject<HTMLElement> | { current: HTMLElement | null };
    key: string;
    title: { en: string; 'zh-CN': string };
    description: { en: string; 'zh-CN': string };
  }>>([]);

  const [showResetOnboardingButton, setShowResetOnboardingButton] = useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    setUser(null);
    navigate(`/login${user?.role === 'Admin' ? '?role=Admin' : ''}`);
    handleClose();
  };

  // Toggle onboarding completion flag in localStorage
  const handleToggleOnboarding = () => {
    const onboardingDone = localStorage.getItem('slideMaster_onboardingDone');
    if (onboardingDone) {
      localStorage.removeItem('slideMaster_onboardingDone');
    } else {
      localStorage.setItem('slideMaster_onboardingDone', 'true');
    }
    handleClose();
    // Restart the app to reflect the change immediately
    window.location.reload();
  };

  const handleResetAllOnboarding = () => {
    localStorage.removeItem('slideMaster_onboardingDone');
    localStorage.removeItem('slideMaster_projectDetailOnboardingDone');
    window.location.reload();
  };

  const menuItems = useMemo(() => ([
    { text: lang === 'en' ? 'Projects' : '项目', icon: <SmartDisplay />, path: '/projects' },
    { text: lang === 'en' ? 'Templates' : '模板', icon: <Folder />, path: '/pptx-files' },
    { text: lang === 'en' ? 'Materials' : '材料', icon: <Description />, path: '/materials' },
    { text: lang === 'en' ? 'Pictures' : '图片', icon: <ImageIcon />, path: '/pictures' },
    { text: lang === 'en' ? 'Tags' : '标签', icon: <Label />, path: '/tags', hidden: !branding?.features?.tagsManagement },
    { text: lang === 'en' ? 'Audit Logs' : '审计', icon: <Article />, path: '/admin/audit-logs', hidden: user?.role === 'Admin' ? undefined : false },
  ]), [lang, branding?.features?.tagsManagement, user?.role]);

  // Initialize onboarding tour
  useEffect(() => {
    if (!user || !showOnboardingTips || tourOpen || tourSteps.length > 0) return;

    const onboardingDone = localStorage.getItem('slideMaster_onboardingDone');
    // Do not show homepage onboarding if already done or if on project detail page
    const isProjectDetailPage = location.pathname.startsWith('/projects/') && location.pathname.split('/').length === 3;

    if (!onboardingDone && location.pathname !== '/' && !isProjectDetailPage) {
      // Build tour steps based on available elements
      const steps: Array<{
        ref: React.RefObject<HTMLElement> | { current: HTMLElement | null };
        key: string;
        title: { en: string; 'zh-CN': string };
        description: { en: string; 'zh-CN': string };
      }> = [];
      
      // Add training materials button if available
      if (branding.trainingMaterialURL && trainingBtnRef.current) {
        steps.push({
          ref: trainingBtnRef,
          key: 'training',
          title: { 
            en: 'Training Materials', 
            'zh-CN': '操作指南' 
          },
          description: { 
            en: 'Access comprehensive guides and tutorials anytime by clicking here!',
            'zh-CN': '点击这里随时查看完整的用户指南和教程！'
          }
        });
      }

      // Add navigation items
      const navDescriptions = [
        { 
          key: 'projects',
          title: { en: 'Projects', 'zh-CN': '项目' },
          icon: <SmartDisplay />,
          description: { 
            en: 'Your presentation projects are organized here. Create and manage all your slides in one place.',
            'zh-CN': '这里是您的工作台，您的演示项目都在这里。项目是您创建的演示文稿的素材集合，可以包含多个幻灯片，材料和图片。'
          }
        },
        { 
          key: 'templates',
          title: { en: 'Templates', 'zh-CN': '模板' },
          icon: <Folder />,
          description: { 
            en: 'Browse and select from professional presentation templates to jumpstart your work.',
            'zh-CN': '浏览并选择专业、规范的预定义演示模板，快速开始您的工作。也可以上传您自己的PPTX文件作为模板。'
          }
        },
        { 
          key: 'materials',
          title: { en: 'Materials', 'zh-CN': '材料' },
          icon: <Description />,
          description: { 
            en: 'Store and organize reference documents that you can use across different projects.',
            'zh-CN': '这里存储和组织可在不同项目中使用的参考文档，支持文本文件、Word 和 PDF等格式。'
          }
        },
        { 
          key: 'pictures',
          title: { en: 'Pictures', 'zh-CN': '图片' },
          icon: <ImageIcon />,
          description: { 
            en: 'Manage your image library for easy access when creating presentations.',
            'zh-CN': `管理您的图片库，在创建演示时轻松访问。${enableImageGeneration ? '你也可以使用AI生成图片。' : ''}`
          }
        },
        { 
          key: 'tags',
          title: { en: 'Tags', 'zh-CN': '标签' },
          icon: <Label />,
          description: { 
            en: 'Organize your content with tags for better searchability and management.',
            'zh-CN': '使用标签组织您的内容，以便更好地搜索和管理。项目、模板、材料和图片都可以添加标签。'
          }
        }
      ];

      menuItems.forEach((item, index) => {
        if (!item.hidden && navItemRefs.current[index]) {
          const navDesc = navDescriptions.find(d => 
            (d.key === 'projects' && item.path === '/projects') ||
            (d.key === 'templates' && item.path === '/pptx-files') ||
            (d.key === 'materials' && item.path === '/materials') ||
            (d.key === 'pictures' && item.path === '/pictures') ||
            (d.key === 'tags' && item.path === '/tags')
          );
          
          if (navDesc) {
            steps.push({
              ref: { current: navItemRefs.current[index] },
              key: navDesc.key,
              title: navDesc.title,
              description: navDesc.description
            });
          }
        }
      });

      if (steps.length > 0) {
        // Small delay to ensure refs are populated
        setTimeout(() => {
          setTourSteps(steps);
          setTourOpen(true);
          setCurrentStep(0);
          // Ensure drawer is open during tour
          setOpen(true);
        }, 500);
      }
    }
  }, [user, showOnboardingTips, location.pathname, branding.trainingMaterialURL, menuItems, tourOpen, tourSteps.length]);

  // Check if onboarding or project detail onboarding is done
  useEffect(() => {
    if (user && showOnboardingTips) {
      const onboardingDone = localStorage.getItem('slideMaster_onboardingDone');
      const projectDetailOnboardingDone = localStorage.getItem('slideMaster_projectDetailOnboardingDone');

      if (onboardingDone || projectDetailOnboardingDone) {
        setShowResetOnboardingButton(true);
      } else {
        setShowResetOnboardingButton(false);
      }
    }
  }, [user, showOnboardingTips]);

  // Tour handlers
  const handleNextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      finishTour();
    }
  };

  const finishTour = () => {
    localStorage.setItem('slideMaster_onboardingDone', 'true');
    setTourOpen(false);
    setCurrentStep(0);
  };

  // Keep drawer open during tour
  useEffect(() => {
    if (tourOpen && !open) {
      setOpen(true);
    }
  }, [tourOpen, open]);

  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/forgot-password' || location.pathname === '/reset-password';
  const isHomePage = location.pathname === '/';

  if (isAuthPage || isHomePage) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flexGrow: 1 }}>
            {location.pathname === '/projects' && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px' }}>
                <img src="/images/product-logo.png" alt="Product Logo" width="auto" height="24" />
              </Box>
            )}
            <Typography variant="h6" noWrap component="div">
              {appTitle}
            </Typography>
          </Stack>
          {user && (
            <div>
              {branding.trainingMaterialURL && (
                <Button
                  ref={trainingBtnRef}
                  variant="outlined"
                  color="inherit"
                  href={branding.trainingMaterialURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mr: 2 }}
                >
                  {lang === 'en' ? 'Training Materials' : '操作指南'}
                </Button>
              )}
              {showResetOnboardingButton && (
                <Tooltip title={lang === 'en' ? 'Reset All Onboarding' : '重置所有指引'} arrow>
                  <IconButton
                    size="large"
                    aria-label="reset onboarding tour"
                    onClick={handleResetAllOnboarding}
                    color="inherit"
                    sx={{ mr: 1 }}
                  >
                    <RestartAlt fontSize="medium" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                edge="end"
              >
                <Person fontSize="medium" />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>
                  <ListItemIcon>
                    <AccountCircle fontSize="small" />
                  </ListItemIcon>
                  {lang === 'en' ? 'Profile' : '个人资料'}
                </MenuItem>
                <Divider />
                {/* <MenuItem onClick={() => { handleClose(); navigate('/account-settings'); }}>
                  <ListItemIcon>
                    <Settings fontSize="small" />
                  </ListItemIcon>
                  {lang === 'en' ? 'Settings' : '设置'}
                </MenuItem> */}
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <ExitToApp fontSize="small" />
                  </ListItemIcon>
                  {lang === 'en' ? 'Logout' : '退出登录'}
                </MenuItem>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {menuItems.filter((item) => item.hidden !== false).map((item, index) => (
            <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
              <Tooltip title={item.text} arrow placement="right">
                <ListItemButton
                  ref={(el) => { navItemRefs.current[index] = el; }}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: [2.5, 4.5],
                  }}
                  onClick={() => navigate(item.path)}
                  selected={location.pathname === item.path}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
        
        {/* Bottom image */}
        {displayProductConcept && (
          <Box sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            alignItems: 'flex-end',
            justifyContent: 'center',
            p: 2,
            mt: 'auto'
          }}>
            <Box
              component="img"
              src="/images/show-three-elements.png"
              alt="Show three elements"
              sx={{
                maxWidth: open ? '70%' : '32px',
                height: 'auto',
                opacity: 0.7,
                filter: 'grayscale(100%)',
                '&:hover': {
                  filter: 'grayscale(0%)',
                  animation: 'spin 8s linear infinite',
                },
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                },
                transition: theme.transitions.create(['opacity', 'max-width', 'filter'], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }}
            />
          </Box>
        )}
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 1 }}>
        <DrawerHeader />
        {children}
      </Box>

      {/* Onboarding Tour Popover */}
      {tourOpen && tourSteps.length > 0 && tourSteps[currentStep] && (
        <Popover
          open={tourOpen}
          anchorEl={tourSteps[currentStep]?.ref.current}
          onClose={() => {}}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          disableRestoreFocus
          disableAutoFocus
          disableEnforceFocus
          sx={{
            pointerEvents: 'none',
            '& .MuiPopover-paper': {
              pointerEvents: 'auto',
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              maxWidth: 360,
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: '8px solid white',
              }
            }
          }}
        >
          <Zoom in={tourOpen} timeout={300}>
            <Paper sx={{ p: 3, position: 'relative' }} elevation={4}>
              {/* Progress indicator */}
              <Box sx={{ 
                position: 'absolute', 
                top: 12, 
                right: 12,
                display: 'flex',
                gap: 0.5,
              }}>
                {tourSteps.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: index === currentStep ? 'primary.main' : 'grey.300',
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
              </Box>

              {/* Content */}
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 4 }}>
                  {tourSteps[currentStep].key === 'training' && <MenuBook sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'projects' && <SmartDisplay sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'templates' && <Folder sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'materials' && <Description sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'pictures' && <ImageIcon sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'tags' && <Label sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '1.1rem',
                    }}
                  >
                    {tourSteps[currentStep].title[lang as 'en' | 'zh-CN']}
                  </Typography>
                </Box>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    lineHeight: 1.6,
                  }}
                >
                  {tourSteps[currentStep].description[lang as 'en' | 'zh-CN']}
                </Typography>

                {/* Actions */}
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    onClick={finishTour}
                    sx={{ color: 'text.secondary' }}
                  >
                    {lang === 'en' ? 'Skip All' : '跳过全部'}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleNextStep}
                    endIcon={currentStep === tourSteps.length - 1 ? <Celebration /> : <ArrowForward />}
                  >
                    {currentStep === tourSteps.length - 1 
                      ? (lang === 'en' ? 'Get Started!' : '开始使用！')
                      : (lang === 'en' ? 'Next' : '下一步')
                    }
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Zoom>
        </Popover>
      )}
    </Box>
  );
};

export default Layout;
